import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { download } from '../../../_helper/file.helper';
import startCase from 'lodash.startcase';
import { exists, formatDateTime, isArray, isDate, isISOString, isObject } from '../../../_helper/util.helper';

/**
 * A generic configurable dialog used to display unstructured json data.
 */
@Component({
    template: `
    <!-- Title and Download/Close -->
    <div mat-dialog-title class="row-between align-items">
      <span>{{data.title}}</span>
      <div>
        <button mat-icon-button color="accent" (click)="download()"><mat-icon>download</mat-icon></button>
        <button mat-icon-button (click)="close()"><mat-icon>close</mat-icon></button>
      </div>
    </div>
    <!-- Search Boxes -->
    <div mat-dialog-content [style.padding-bottom]="0">
      <div class="grid-col-2 w-100">
        <!-- Search Labels -->
        <mat-form-field appearance="outline">
          <input matInput #keyLabelFilter [placeholder]="data.transform ? 'Labels' : 'Keys'" (input)="filter(data.transform ? '' : keyLabelFilter.value, data.transform ? keyLabelFilter.value : '', valueFilter.value)" />
          <mat-icon matSuffix *ngIf="keyLabelFilter.value" class="pointer" (click)="keyLabelFilter.value=''; filter('', '', valueFilter.value)">close</mat-icon>
          <mat-icon matSuffix *ngIf="!keyLabelFilter.value">search</mat-icon>
        </mat-form-field>
        <!-- Search Values -->
        <mat-form-field appearance="outline">
          <input matInput #valueFilter placeholder="Values" (input)="filter(data.transform ? '' : keyLabelFilter.value, data.transform ? keyLabelFilter.value : '', valueFilter.value)" />
          <mat-icon matSuffix *ngIf="valueFilter.value" class="pointer" (click)="valueFilter.value=''; filter(data.transform ? '' : keyLabelFilter.value, data.transform ? keyLabelFilter.value : '', '')">close</mat-icon>
          <mat-icon matSuffix *ngIf="!valueFilter.value">search</mat-icon>
        </mat-form-field>
      </div>
    </div>
    <!-- Json Pretty Printed -->
    <div mat-dialog-content>
      <div class="grid-col-2 w-100">
        <ng-container *ngFor="let d of filteredDatas">
          <div>{{data.transform ? d.label : d.key}}</div>
          <div>{{d.value}}</div>
        </ng-container>
      </div>
    </div>
    <!-- Also Close -->
    <div mat-dialog-actions class="row-end">
      <button mat-flat-button (click)="close()">
        Close
      </button>
    </div>
  `,
    styleUrls: ['./_dialog.scss'],
    standalone: false
})
export class JsonDialog {

  public datas: { key: string, label: string, value: string }[] = [];
  public filteredDatas: { key: string, label: string, value: string }[] = [];

  public string: string = '';

  constructor(
    private self: MatDialogRef<JsonDialog>,
    @Inject(MAT_DIALOG_DATA) public data: {
      title: string;
      json: any;
      transform: boolean;
      sort: boolean;
      filter: boolean;
      format: boolean;
    },
  ) { }

  ngOnInit() {
    for (let key in this.data.json) {
      if (!this.data.filter || isArray(this.data.json[key]) ? this.data.json[key].length : exists(this.data.json[key])) {
        let value = this.data.format ? this.formatValue(this.data.json[key]) : JSON.stringify(this.data.json[key], null, 2).replace(/\"([^"]+)\"/g, '$1');
        this.datas.push({
          key: key,
          label: startCase(key),
          value: value
        });
      }
    }
    let sortKey = this.data.transform ? 'label' : 'key';

    if (this.data.sort)
      this.datas.sort((a, b) => a[sortKey] == b[sortKey] ? 0 : a[sortKey] > b[sortKey] ? 1 : -1);

    let pretty = {};
    for (let data of this.datas)
      pretty[this.data.transform ? data.label : data.key] = data.value;

    this.string = JSON.stringify(this.data.transform ? pretty : this.data.json);
    this.filter();
  }

  private formatValue(value: any): string {
    if (typeof value === 'string') {
      if (isISOString(value)) {
        return formatDateTime(value);
      } else {
        return ''+value;
      }
    } else if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    } else if (typeof value === 'object') {
      if (isDate(value)) {
        return formatDateTime(value);
      } else if (isArray(value)) {
        if (value.length && typeof value[0] === 'object')
          for (let i = 0; i < value.length; i++)
            value[i] = JSON.stringify(value);
        return value.join(', ');
      } else if (isObject(value)) {
        return JSON.stringify(value, null, 2);
      }
    }
    return ''+value;
  }

  public filter(key?: string, label?: string, value?: string) {
    if (key || label || value) {
      this.filteredDatas = this.datas.filter(d =>
        (!key   || (''+d.key).toUpperCase().includes(key  .trim().toUpperCase())) &&
        (!label || (''+d.label).toUpperCase().includes(label.trim().toUpperCase())) &&
        (!value || (''+d.value).toUpperCase().includes(value.trim().toUpperCase()))
      );
    } else this.filteredDatas = this.datas;
  }

  public download() {
    download(this.data.title+'.json', this.string);
  }

  public close() {
    this.self.close();
  }
}

export const JsonDialogConfig = {
  width: '40rem',
  maxWidth: '95vw',
  maxHeight: '95vh'
}
